import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/components/ui/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/AppSidebar'
import NavBar from '@/components/NavBar'
import { useUserRole } from '@/hooks/useUserRole'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from '@/components/ui/badge'
import { Check, Users } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

interface Team {
  id: string
  name: string
  subscription_status: string | null
  subscription_tier: string | null
  seats_limit: number
}

interface TeamMember {
  id: string
  role: string
  user: {
    first_name: string | null
    last_name: string | null
    email: string
    id: string
  }
}

const PRICING_TIERS = [
  {
    name: 'ERP CoPilot AI',
    id: 'erp_copilot',
    price: '$1',
    description: 'AI-powered ERP assistant',
    features: [
      'Unlimited AI interactions',
      'Advanced analytics',
      'Priority support',
      'Full feature access',
      'Team collaboration',
      'Additional seats available',
    ],
    seats: 1,
  },
] as const

export default function Billing() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [team, setTeam] = useState<Team | null>(null)
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [newMemberEmail, setNewMemberEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const { toast } = useToast()
  const navigate = useNavigate()
  const { role, isLoading: isRoleLoading } = useUserRole()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const initialize = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) {
        navigate('/')
        return
      }
      setIsAuthenticated(true)
      await loadTeamData()
    }

    initialize()

    // Check for successful payment
    const success = searchParams.get('success')
    const sessionId = searchParams.get('session_id')
    if (success === 'true' && sessionId) {
      // Update team data after successful payment
      loadTeamData().then(() => {
        toast({
          title: 'Success',
          description: 'Your subscription has been activated successfully!',
        })
      })
    } else if (success === 'false') {
      toast({
        title: 'Cancelled',
        description: 'Subscription process was cancelled.',
        variant: 'destructive',
      })
    }
  }, [navigate, searchParams])

  const loadTeamData = async () => {
    try {
      console.log('Loading team data...')
      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .select('*')
        .single()

      if (teamError) {
        if (teamError.code === 'PGRST116') {
          const { data: { user } } = await supabase.auth.getUser()
          if (!user) throw new Error('No user found')

          const { data: newTeam, error: createError } = await supabase
            .from('teams')
            .insert([{
              name: `${user.email}'s Team`,
              owner_id: user.id,
              seats_limit: 1,
            }])
            .select()
            .single()

          if (createError) throw createError
          setTeam(newTeam)

          await supabase
            .from('team_members')
            .insert([{
              team_id: newTeam.id,
              user_id: user.id,
              role: 'owner',
            }])
        } else {
          throw teamError
        }
      } else {
        console.log('Team data loaded:', teamData)
        setTeam(teamData)
      }

      // Load team members
      if (teamData) {
        const { data: membersData, error: membersError } = await supabase
          .from('team_members')
          .select(`
            id,
            role,
            user:profiles!inner (
              first_name,
              last_name,
              id,
              email
            )
          `)
          .eq('team_id', teamData.id)

        if (membersError) throw membersError
        setTeamMembers(membersData as TeamMember[])
      }
    } catch (error) {
      console.error('Error loading team data:', error)
      toast({
        title: 'Error',
        description: 'Failed to load team data',
        variant: 'destructive',
      })
    }
  }

  const getSeatsLimit = () => {
    if (!team) return 1;
    // If there's no subscription, return base limit of 1
    if (!team.subscription_status || team.subscription_status !== 'active') {
      return 1;
    }
    // Return the team's seat limit for subscribed teams
    return team.seats_limit || 1;
  }

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!team) return

    try {
      setIsLoading(true)
      console.log('Starting invite process for:', newMemberEmail)

      const currentSeatsLimit = getSeatsLimit();

      if (teamMembers.length >= currentSeatsLimit) {
        toast({
          title: 'Additional Seat Required',
          description: 'Please subscribe to add more team members. Each additional seat is $1/month.',
          variant: 'destructive',
        })
        setInviteDialogOpen(false) // Close the invite dialog
        return
      }

      if (teamMembers.length >= (team.seats_limit || 1)) {
        toast({
          title: 'Error',
          description: 'No available seats. Please upgrade your subscription to add more team members.',
          variant: 'destructive',
        })
        return
      }

      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', newMemberEmail)
        .single()

      if (userError) {
        console.log('User not found, sending invitation email')
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('No authenticated user found')

        const { data: inviterProfile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', user.id)
          .single()

        const inviterName = inviterProfile 
          ? `${inviterProfile.first_name || ''} ${inviterProfile.last_name || ''}`.trim() 
          : user.email

        const { error: emailError } = await supabase.functions.invoke('send-team-invite', {
          body: {
            email: newMemberEmail,
            teamName: team.name,
            inviterName,
          },
        })

        if (emailError) {
          console.error('Error sending invitation:', emailError)
          throw new Error('Failed to send invitation email. Please try again.')
        }

        toast({
          title: 'Invitation Sent',
          description: 'An invitation email has been sent to the user.',
        })
        setNewMemberEmail('')
        setInviteDialogOpen(false)
        return
      }

      const { data: existingMember } = await supabase
        .from('team_members')
        .select('id')
        .eq('team_id', team.id)
        .eq('user_id', userData.id)
        .single()

      if (existingMember) {
        toast({
          title: 'Error',
          description: 'This user is already a team member',
          variant: 'destructive',
        })
        return
      }

      const { error: addError } = await supabase
        .from('team_members')
        .insert([{
          team_id: team.id,
          user_id: userData.id,
          role: 'member',
        }])

      if (addError) throw addError

      toast({
        title: 'Success',
        description: 'Team member added successfully',
      })

      setNewMemberEmail('')
      setInviteDialogOpen(false)
      await loadTeamData()
    } catch (error) {
      console.error('Error inviting team member:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add team member',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubscribe = async (tierId: string) => {
    if (!team) return

    try {
      setIsLoading(true)
      console.log('Starting subscription process...')

      const { data, error } = await supabase.functions.invoke('stripe-billing', {
        body: {
          action: 'create-subscription',
          teamId: team.id,
          tier: tierId,
        },
      })

      if (error) throw error
      if (!data?.url) throw new Error('No checkout URL received')

      // Redirect to Stripe Checkout
      window.location.href = data.url
    } catch (error) {
      console.error('Error creating subscription:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create subscription',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (isRoleLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>
  }

  if (role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-[400px]">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You need admin privileges to access the billing page.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 relative bg-gray-50">
          <NavBar onSignOut={() => {}} isAuthenticated={isAuthenticated} />
          <div className="p-8 pt-24">
            <div className="max-w-6xl mx-auto space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle>Team Management</CardTitle>
                  <CardDescription>
                    Manage your team members and subscription
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-semibold">Team Members</h3>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-gray-500" />
                          <p className="text-sm text-gray-500">
                            {teamMembers.length} / {getSeatsLimit()} seats used
                          </p>
                        </div>
                        {teamMembers.length >= getSeatsLimit() && (
                          <p className="text-sm text-yellow-600 mt-1">
                            {team?.subscription_status === 'active' 
                              ? "You've reached your seat limit. Subscribe to add more members." 
                              : "Subscribe to add more team members. Each additional seat is $1/month."}
                          </p>
                        )}
                      </div>
                      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                        <DialogTrigger asChild>
                          <Button disabled={teamMembers.length >= getSeatsLimit()}>
                            Invite Member
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Invite Team Member</DialogTitle>
                            <DialogDescription>
                              {teamMembers.length >= getSeatsLimit() 
                                ? "You need to purchase additional seats to invite more members."
                                : "Enter the email address of the person you want to invite."}
                            </DialogDescription>
                          </DialogHeader>
                          <form onSubmit={handleInviteMember} className="space-y-4 pt-4">
                            <div className="space-y-2">
                              <Label htmlFor="email">Email Address</Label>
                              <Input
                                id="email"
                                type="email"
                                placeholder="colleague@company.com"
                                value={newMemberEmail}
                                onChange={(e) => setNewMemberEmail(e.target.value)}
                                required
                              />
                            </div>
                            <Button
                              type="submit"
                              disabled={isLoading || !newMemberEmail || teamMembers.length >= getSeatsLimit()}
                              className="w-full"
                            >
                              {isLoading ? 'Adding member...' : 'Add Member'}
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {teamMembers.map((member) => (
                          <TableRow key={member.id}>
                            <TableCell>
                              {member.user.first_name} {member.user.last_name}
                            </TableCell>
                            <TableCell>{member.user.email}</TableCell>
                            <TableCell>
                              <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>
                                {member.role}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              <div className="max-w-lg mx-auto">
                {PRICING_TIERS.map((tier) => (
                  <Card key={tier.id} className="border-primary">
                    <CardHeader>
                      <CardTitle>{tier.name}</CardTitle>
                      <CardDescription>{tier.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div>
                        <span className="text-3xl font-bold">{tier.price}</span>
                        <span className="text-gray-500">/month per seat</span>
                      </div>
                      <ul className="space-y-2">
                        {tier.features.map((feature) => (
                          <li key={feature} className="flex items-center gap-2">
                            <Check className="h-4 w-4 text-green-500" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        className="w-full mt-6"
                        variant={tier.id === team?.subscription_tier ? 'outline' : 'default'}
                        disabled={tier.id === team?.subscription_tier || isLoading}
                        onClick={() => handleSubscribe(tier.id)}
                      >
                        {tier.id === team?.subscription_tier
                          ? 'Current Plan'
                          : team?.subscription_status === 'active'
                          ? 'Add Seats'
                          : 'Subscribe Now'}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>
  )
}
